import classNames from "classnames";
import isEmpty from "is-empty";
import React, { useEffect, useLayoutEffect, useState } from "react";
import ReactPixel from "react-facebook-pixel";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import {
  redirectPaymentGatewayForCartMutation,
  updateOccupantMutation,
} from "rsl-api-106/lib/mutations";
import { getBookingProductsQuery, getCartQuery } from "rsl-api-106/lib/queries";
import {
  BookingStatus,
  CartPaymentMethodInput,
  CustomerBooking,
  Mutation,
  MutationRedirectPaymentGatewayForCartArgs,
  MutationUpdateOccupantArgs,
  ProductByCategory,
  ProductDistributionType,
  Query,
  QueryGetBookingProductsArgs,
  QueryGetCartArgs,
} from "rsl-api-106/lib/types";

import { useApolloClient } from "@apollo/client";
import Alert from "@components/_Layout/Alert";
import Button from "@components/_Layout/Controls/Button";
import LinkButton from "@components/_Layout/Controls/LinkButton";
import Popup from "@components/_Layout/Controls/Popup";
import LoadingScreen from "@components/_Layout/Loaders/LoadingScreen";
import ProductList from "@components/Products/ProductList";
import BasketSummary from "@components/Reservation/BasketSummary";
import { PopupEdit } from "@components/Reservation/BasketSummary/BasketSummary";
import StepAuth from "@components/Reservation/Steps/StepAuth";
import StepPayment from "@components/Reservation/Steps/StepPayment";
import StepReservationContent from "@components/Reservation/Steps/StepReservationContent";
import {
  GTMReservationPageStep1,
  GTMReservationPageStep2,
  GTMReservationPageStep3,
  GTMReservationPageStep4,
} from "@components/Tracking/GTMReservationPage";
import {
  useAuth,
  useCart,
  useConfig,
  useLoadingScreen,
  useScrollTop,
  useSession,
} from "@hooks";
import { getCurrencySymbol } from "@utils/currencies";
import dateFormat from "@utils/dateformat";
import { focusElementById, getAbsoluteImagePath, scrollTop } from "@utils/dom";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "@utils/localStorage";
import { getCustomerUrlByWebUser, redirectTo } from "@utils/tools";
import { trackClick } from "@utils/tracking/gtm";

import { PspCallbackStatus, TErrorsSteps } from "../types";

type ReservationPageProps = {
  //
};
enum StepsEnum {
  AUTH = 0,
  ACTIVITIES = 1,
  OCCUPANT = 2,
  PAYMENT = 3,
}
const languagesVarsCodes = [
  "LANG_neobooking_step_reservation", //Réservation
  "LANG_neobooking_step_auth", //Authentification
  "LANG_neobooking_step_payment", //Paiement
  "LANG_neobooking_step_presta", //Préstation
  "LANG_neobooking_back", //Retour
  "LANG_neobooking_back_previous_step", //Revenir à l'étape précédente
  "LANG_neobooking_next", //Suivant
  "LANG_neobooking_psp_success", //Votre paiement a été validée.
  "LANG_neobooking_psp_decline", //Votre paiement a été décliné.
  "LANG_neobooking_psp_cancel", //Votre paiement a été annulé.
  "LANG_neobooking_psp_exception", //Une erreur est survenue lors de votre paiement.
  "LANG_neobooking_complete_all_occupants_details", //Veuillez renseigner et enregistrer les informations de tout les occupants.
  "LANG_neobooking_auth_required_to_continue", //Vous devez vous connecter afin de poursuivre votre réservation.
  "LANG_neobooking_payment_due_required", //Vous devez choisir un type d'échéance.
  "LANG_neobooking_please_wait", //Veuillez patienter quelques instants
  "LANG_neobooking_cart_empty", //Votre panier est vide
  "LANG_neobooking_cart_empty_description", //Ajoutez un séjour pour poursuivre votre réservation.
  "LANG_neobooking_to_date", //Au
  "LANG_neobooking_nights", //Nuits
  "LANG_neobooking_insurance_holiday", //ASSUREZ VOS VACANCES
  "LANG_neobooking_select_insurance", //Veuillez sélectionner 1 option d'assurance.
  "LANG_neobooking_info_insurance", //Pour rappel, les assurances comprennent
  "LANG_neobooking_insurance_cancellation_stay", //Annulation et interruption de séjour (décès, maladie, hospitalisation, dommages grave de votre véhicule
  "LANG_neobooking_insurance_changes_vacation_dates", //Suppression ou modification des dates de congés pays du fait de l'employeur
  "LANG_neobooking_insurance_job_transfer", //Mutation professionnelle, obtention d'un emploi
  "LANG_neobooking_insurance_covid", //COVID-19 (cas contact, contamination, maladie)
  "LANG_neobooking_make_my_choice", //Je fais mon choix
  "LANG_neobooking_continue_without_insurance", //Continuer sans assurance
  "LANG_neobooking_no_dispo_message", //L'hébergement sélectionné n'est plus disponible. Merci d'effectuer une nouvelle recherche.
  "LANG_neobooking_new_search", //Nouvelle recherche
];
/**
 * Given a reservation, return the name of the rooms that are part of the reservation
 * @param {CustomerBooking} reservation - Reservation
 * @returns The name of the rooms that are being reserved.
 */
const getReservationRoomsInfosName = (reservation: CustomerBooking) => {
  let title = "";
  reservation?.stays?.forEach((stay, stayIndex) => {
    return (
      <div>
        {" "}
        {stay?.distribution?.roomTypes?.forEach((roomInfo, roomInfoIndex) => {
          title =
            title +
            `${stayIndex === 0 ? "" : "+ "} ${roomInfo?.quantity}x ${
              roomInfo?.label ?? ""
            } `;
        })}
      </div>
    );
  });
  return title;
};
type ResaStep = {
  label: string;
  show: boolean;
};
const FastCheckinPage: React.FC<ReservationPageProps> = () => {
  /* Hooks */
  //on scroll top automatiquement en haut de page au chargement du composant
  useScrollTop();
  const client = useApolloClient();
  const { sessionId, getLanguageVarValue, lang } = useSession();
  const { user, loading: userLoading, signout } = useAuth();
  const {
    cart,
    save,
    addProposalFromKeys,
    loading: loadingCart,
    confirmFastCheckout,
  } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();

  const { mode: clientMode, logo: clientLogo } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";
  const logo = clientLogo ? clientLogo : "Vacanceole-B-RVB.png";

  //steps state
  const stepParam: string | null = searchParams.get("step");
  const [activeStep, setActiveStep] = useState<number>(
    stepParam
      ? parseInt(stepParam)
      : getLocalStorageItem("rsl_step")
      ? parseInt(getLocalStorageItem("rsl_step")!!)
      : 0
  ); //step param First, else localStorage step value, else 0
  const [errorsSteps, setErrorsSteps] = useState<TErrorsSteps[]>([]);
  const [activeErrorsSteps, setActiveErrorsSteps] =
    useState<TErrorsSteps | undefined>(undefined);

  //terms state
  const [termsChecked, setTermsChecked] = useState<boolean>(false);
  const [termsError, setTermsError] = useState<boolean>(false);
  //payment state

  //const [selectedPaymentDueType, setSelectedPaymentDueType] = useState<string | undefined>(undefined);

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string | undefined>(undefined);
  const [selectedPaymentOption, setSelectedPaymentOption] =
    useState<string | undefined>(undefined);
  //insurance state
  const [selectedInsurance, setSelectedInsurance] =
    useState<string | undefined>(undefined);

  const [showPopupNoInsurance, setShowPopupNoInsurance] =
    useState<boolean>(false);
  //skip prestation occupant
  const [hasPresta, setHasPresta] = useState<boolean>(true);
  //skip prestation accommodation/stay
  const [hasPrestaStay, setHasPrestaStay] = useState<boolean>(true);
  //saving state
  const [isSaving, setIsSaving] = useState<boolean>(false);

  //fast chechin / checkout state
  const [selectedFastcheckout, setSelectedFastcheckout] =
    useState<boolean>(true);

  const enableFCI =
    (process.env.REACT_APP_ENABLEFCI ?? "false") === JSON.stringify(true);

  const isFromFastCheckinOrCheckout = cart?.fromFastCheckinCheckout
    ? cart.fromFastCheckinCheckout && enableFCI
    : false;

  const stepFast: ResaStep[] = [
    {
      label: lang.toUpperCase() === "FR" ? "Voyageurs" : "Travellers",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Prestations" : "Products",
      show: true,
    },
    {
      label: lang.toUpperCase() === "FR" ? "Caution" : "Caution",
      show: true,
    },
  ];

  const [steps, setSteps] = useState<ResaStep[]>(stepFast);
  const [mountedSteps, setMountedSteps] = useState<boolean>(false);

  const [savingStep, setSavingStep] = useState<boolean>(false);

  useEffect(() => {
    /*Facebook Pixel Code Tracking*/
    ReactPixel.init(process.env.REACT_APP_GOOGLE_PIXEL_CODE!!);
    ReactPixel.track("AddToCart");
  }, []);

  useLayoutEffect(() => {
    const init = async () => {
      setMountedSteps(true);
    };
    if (cart) {
      if (!loadingCart) {
        init();
        setSelectedInsurance(
          getLocalStorageItem("product_insurance") ?? undefined
        );
      }
      //setSelectedPaymentDueType(cart?.reservations?.[0]?.selectedPaymentDue ?? undefined);
    }
  }, [cart, loadingCart]);

  useEffect(() => {
    if (cart && !isEmpty(cart.reservations)) {
      const pspStatus = searchParams.get("s");
      let pspMessage = searchParams.get("pspCallbackMessage");
      if (pspStatus && !isFromFastCheckinOrCheckout) {
        switch (pspStatus as PspCallbackStatus) {
          case "s":
            pspMessage = pspMessage
              ? pspMessage
              : getLanguageVarValue("LANG_neobooking_psp_success");
            break;
          case "d":
            pspMessage = pspMessage
              ? pspMessage
              : getLanguageVarValue("LANG_neobooking_psp_decline");
            break;
          case "c":
            pspMessage = pspMessage
              ? pspMessage
              : getLanguageVarValue("LANG_neobooking_psp_cancel");
            break;
          case "e":
            pspMessage = pspMessage
              ? pspMessage
              : getLanguageVarValue("LANG_neobooking_psp_exception");
            break;
        }
        setActiveStep(StepsEnum.PAYMENT);
        setErrorsSteps([
          {
            stepIndex: StepsEnum.PAYMENT,
            variant: "warning",
            children: <>{pspMessage}</>,
          },
        ]);
      }
    } else {
      if (isFromFastCheckinOrCheckout) {
        setActiveStep(StepsEnum.OCCUPANT);
      }
      setActiveStep(StepsEnum.AUTH);
    }
  }, [cart]);

  useEffect(() => {
    if (!mountedSteps) {
      if (stepParam) {
        setActiveStep(parseInt(stepParam));
      } else if (getLocalStorageItem("rsl_step")) {
        setActiveStep(parseInt(getLocalStorageItem("rsl_step")!!));
      }
      removeLocalStorageItem("rsl_step");
    }
  }, [steps, mountedSteps]);

  useEffect(() => {
    setActiveErrorsSteps(
      [...errorsSteps].find((x) => x.stepIndex === activeStep)
    );
  }, [errorsSteps, activeStep]);

  const showInsurance =
    (cart?.reservations?.[0]?.id ? false : true) ||
    (searchParams.get("pi") && searchParams.get("pi") === "true")
      ? true
      : false;

  const handlePreviousStep = () => {
    let category =
      activeStep === StepsEnum.ACTIVITIES
        ? "Options"
        : activeStep === StepsEnum.OCCUPANT
        ? "Occupant"
        : activeStep === StepsEnum.AUTH
        ? "Authentification"
        : "Paiement";
    trackClick(
      `Tunnel - ${category}`,
      "click bouton",
      "Bouton - Précédent",
      ""
    );

    if (!isFromFastCheckinOrCheckout) {
      if (activeStep === StepsEnum.PAYMENT) {
        if (hasPresta && hasPrestaStay) {
          setActiveStep(StepsEnum.OCCUPANT);
        } else if (!hasPresta && !hasPrestaStay) {
          setActiveStep(StepsEnum.AUTH);
        } else if (!hasPresta && hasPrestaStay) {
          setActiveStep(StepsEnum.ACTIVITIES);
        } else if (hasPresta && !hasPrestaStay) {
          setActiveStep(StepsEnum.OCCUPANT);
        }
      } else if (activeStep === StepsEnum.OCCUPANT) {
        if (!hasPrestaStay && hasPresta) {
          setActiveStep(StepsEnum.AUTH);
        } else if (hasPrestaStay && !hasPresta) {
          setActiveStep(StepsEnum.ACTIVITIES);
        } else if (!hasPresta && !hasPrestaStay) {
          setActiveStep(StepsEnum.AUTH);
        } else if (hasPresta && hasPrestaStay) {
          setActiveStep(StepsEnum.ACTIVITIES);
        }
      } else if (activeStep === StepsEnum.ACTIVITIES) {
        setActiveStep(StepsEnum.AUTH);
      }
    } else {
      if (activeStep === 2) {
        setActiveStep(1);
      } else if (activeStep === 1) {
        if (hasPresta) {
          setActiveStep(0);
        }
      }
    }
  };

  const getWebUser = () => {
    const localWebuser = getLocalStorageItem("webuser");
    return (
      searchParams.get("webuser") ??
      searchParams.get("wu") ??
      localWebuser ??
      process.env.REACT_APP_WEBUSER!!
    );
  };

  const handleNextStep = async (skipInsurance?: boolean | undefined) => {
    let _errorsSteps: TErrorsSteps[] = [...errorsSteps].filter(
      (x) => x.stepIndex !== activeStep
    );

    if (!isFromFastCheckinOrCheckout) {
      switch (activeStep) {
        case StepsEnum.ACTIVITIES:
          setActiveStep(hasPresta ? StepsEnum.OCCUPANT : StepsEnum.PAYMENT);
          trackClick(
            "Tunnel - Occupant",
            "click bouton",
            "Bouton - Suivant",
            ""
          );
          break;
        case StepsEnum.OCCUPANT:
          const { valid, controlToFocus, hasPrestaOccupant } = checkOccupant();
          if (hasPrestaOccupant && !valid) {
            toast.error(
              getLanguageVarValue(
                "LANG_neobooking_complete_all_occupants_details"
              )
            );
            _errorsSteps.push({
              stepIndex: activeStep,
              variant: "warning",
              children: (
                <>
                  {getLanguageVarValue(
                    "LANG_neobooking_complete_all_occupants_details"
                  )}
                </>
              ),
            });
            scrollTop();
            focusElementById(controlToFocus);
          } else {
            setActiveStep(activeStep + 1);
            trackClick(
              "Tunnel - Paiement",
              "click bouton",
              "Bouton - Suivant",
              ""
            );
          }
          setErrorsSteps(_errorsSteps);
          break;
        case StepsEnum.AUTH:
          if (isEmpty(user)) {
            toast.error(
              getLanguageVarValue("LANG_neobooking_auth_required_to_continue")
            );
            _errorsSteps.push({
              stepIndex: activeStep,
              variant: "warning",
              children: (
                <>
                  {getLanguageVarValue(
                    "LANG_neobooking_auth_required_to_continue"
                  )}
                </>
              ),
            });
          } else {
            if (hasPresta && hasPrestaStay) {
              setActiveStep(StepsEnum.ACTIVITIES);
            } else if (!hasPresta && !hasPrestaStay) {
              setActiveStep(StepsEnum.PAYMENT);
            } else if (!hasPresta && hasPrestaStay) {
              setActiveStep(StepsEnum.ACTIVITIES);
            } else if (hasPresta && !hasPrestaStay) {
              setActiveStep(StepsEnum.OCCUPANT);
            }
            trackClick(
              "Tunnel - Options",
              "click bouton",
              "Bouton - Suivant",
              ""
            );
          }
          setErrorsSteps(_errorsSteps);
          break;
        case StepsEnum.PAYMENT:
          let stepErrorMessage = "";
          if (!termsChecked) {
            stepErrorMessage += getLanguageVarValue(
              "LANG_neobooking_terms_required"
            );
            setTermsError(true);
          } else {
            setErrorsSteps(_errorsSteps);
          }
          if (!isEmpty(stepErrorMessage)) {
            toast.error(stepErrorMessage);
            _errorsSteps.push({
              stepIndex: activeStep,
              variant: "warning",
              children: <>{stepErrorMessage}</>,
            });
            setErrorsSteps(_errorsSteps);
            return;
          }

          /*if (isEmpty(selectedPaymentDueType)) {
            stepErrorMessage += `${getLanguageVarValue(
              "LANG_neobooking_payment_due_required"
            )} `;
          }*/
          if (!isEmpty(stepErrorMessage)) {
            toast.error(stepErrorMessage);
            _errorsSteps.push({
              stepIndex: activeStep,
              variant: "warning",
              children: <>{stepErrorMessage}</>,
            });
            setErrorsSteps(_errorsSteps);
          } else {
            setTermsError(false);
            setIsSaving(true);
            const reservationUrl =
              window.location.origin +
              `/reservation?step=${(steps.length - 1).toString()}`;
            const confirmationUrl = window.location.origin + "/confirmation";

            const reservationId = cart?.reservations!![0]?.id;

            //on reprend le param initial, qu'on repasse au callback
            const showpi = false;

            const acceptUrl = `${confirmationUrl}?s=s&r=${reservationId}&wu=${getWebUser()}`;
            const declineUrl = `${reservationUrl}&s=d&session=${sessionId}&r=${reservationId}&pi=${showpi.toString()}&wu=${getWebUser()}`;
            const cancelUrl = `${reservationUrl}&s=c&session=${sessionId}&r=${reservationId}&pi=${showpi.toString()}&wu=${getWebUser()}`;
            const exceptionUrl = `${reservationUrl}&s=e&session=${sessionId}&r=${reservationId}&pi=${showpi.toString()}&wu=${getWebUser()}`;

            const isAutomaticPayment = true;
            const input: CartPaymentMethodInput = {
              bookingStatus: BookingStatus.Guaranteed,
              ccardType: parseInt(selectedPaymentOption ?? "1"),
              paymentMode: "CB",
              paymentOptions: "CB",
              automaticPayment: true, //cart.payment.isAuto
              //manualPaymentOptions: null,
              //paymentModeSetOnlyFirst: null,
              //paymentAmount: null,
              //chequeNumber: null,
              url: {
                acceptUrl,
                declineUrl,
                cancelUrl,
                exceptionUrl,
              },
            };

            //Avant de save ou de redirect sur psp, on check les values de l'occupant
            try {
              const { data: cartData } = await client.query<
                Query,
                QueryGetCartArgs
              >({
                query: getCartQuery(),
                variables: {
                  session: { name: sessionId },
                },
                fetchPolicy: "no-cache",
              });

              if (
                cartData &&
                cartData.getCart &&
                cartData.getCart.cart &&
                cartData.getCart.cart &&
                cartData.getCart.cart.reservations &&
                cartData.getCart.cart.reservations.length > 0
              ) {
                //si on a un cart, on check les values de l'occupant
                const lastname =
                  cartData.getCart.cart.reservations[0]?.primaryOccupant
                    ?.lastName;
                const isLastnameEmpty = isEmpty(lastname);
                //Si on a un lastname primary occupant vide, on fait un updateOccupant
                if (isLastnameEmpty) {
                  const { data: dataUpdateOccupant, errors } =
                    await client.mutate<Mutation, MutationUpdateOccupantArgs>({
                      mutation: updateOccupantMutation(),
                      variables: {
                        session: { name: sessionId },
                        input: {
                          occupantGuiid:
                            cartData.getCart.cart.reservations[0]?.stays?.[0]
                              ?.occupants?.[0]?.guiid!!,
                          firstName: user?.firstName,
                          lastName: user?.lastName,
                          birthDate: user?.birthDate,
                        },
                      },
                      fetchPolicy: "no-cache",
                    });
                }
              }
            } catch (error) {
              console.log(error);
            }

            if (isAutomaticPayment) {
              setLocalStorageItem("rsl_step", StepsEnum.PAYMENT.toString());
              client
                .mutate<Mutation, MutationRedirectPaymentGatewayForCartArgs>({
                  mutation: redirectPaymentGatewayForCartMutation(),
                  variables: {
                    session: { name: sessionId!! },
                    input: { input: input },
                  },
                  fetchPolicy: "no-cache",
                })
                .then((response) => {
                  if (response.data?.redirectPaymentGatewayForCart?.gateway) {
                    setIsSaving(false);
                    ReactPixel.track("InitiateCheckout");
                    trackClick(
                      "Tunnel - Paiement",
                      "click bouton",
                      "Bouton - Réserver",
                      ""
                    );
                    window.location.href =
                      response.data.redirectPaymentGatewayForCart?.gateway;
                  }
                })
                .catch((err) => {
                  toast.error(err.message);
                  _errorsSteps.push({
                    stepIndex: activeStep,
                    variant: "warning",
                    children: <>{err.message}</>,
                  });
                  setErrorsSteps(_errorsSteps);
                  setIsSaving(false);
                });
            } else {
              save(false, cart?.fromFastCheckinCheckout ?? false, (err) => {
                if (err) {
                  toast.error(err.message);
                  _errorsSteps.push({
                    stepIndex: activeStep,
                    variant: "warning",
                    children: <>{err.message}</>,
                  });
                  setErrorsSteps(_errorsSteps);
                  setIsSaving(false);
                } else {
                  trackClick(
                    "Tunnel - Paiement",
                    "click bouton",
                    "Bouton - Réserver",
                    ""
                  );
                  ReactPixel.track("InitiateCheckout");
                  window.location.href = confirmationUrl;
                }
              });
            }
          }
          break;
      }
    } else {
      switch (activeStep) {
        case 0:
          const { valid, controlToFocus, hasPrestaOccupant } = checkOccupant();
          if (!valid) {
            toast.error(
              getLanguageVarValue(
                "LANG_neobooking_complete_all_occupants_details"
              )
            );
            _errorsSteps.push({
              stepIndex: activeStep,
              variant: "warning",
              children: (
                <>
                  {getLanguageVarValue(
                    "LANG_neobooking_complete_all_occupants_details"
                  )}
                </>
              ),
            });
            scrollTop();
            focusElementById(controlToFocus);
          } else {
            setActiveStep(activeStep + 1);
            trackClick(
              "Tunnel - Paiement",
              "click bouton",
              "Bouton - Suivant",
              ""
            );
          }
          setErrorsSteps(_errorsSteps);
          break;
        case 1:
          setActiveStep(activeStep + 1);
          break;
        case 2:
          //console.log(selectedInsurance);
          //console.log(skipInsurance);
          let stepErrorMessage = "";
          if (!termsChecked) {
            stepErrorMessage += getLanguageVarValue(
              "LANG_neobooking_terms_required"
            );
            setTermsError(true);
          } else {
            setErrorsSteps(_errorsSteps);
          }
          if (!isEmpty(stepErrorMessage)) {
            toast.error(stepErrorMessage);
            _errorsSteps.push({
              stepIndex: activeStep,
              variant: "warning",
              children: <>{stepErrorMessage}</>,
            });
            setErrorsSteps(_errorsSteps);
            return;
          }

          /*if (isEmpty(selectedPaymentDueType)) {
            stepErrorMessage += `${getLanguageVarValue(
              "LANG_neobooking_payment_due_required"
            )} `;
          }*/
          if (!isEmpty(stepErrorMessage)) {
            toast.error(stepErrorMessage);
            _errorsSteps.push({
              stepIndex: activeStep,
              variant: "warning",
              children: <>{stepErrorMessage}</>,
            });
            setErrorsSteps(_errorsSteps);
          } else {
            setTermsError(false);
            setIsSaving(true);
            const reservationUrl =
              window.location.origin +
              `/reservation/pre-checkin?step=${(steps.length - 1).toString()}`;
            const confirmationUrl = window.location.origin + "/confirmation";

            const reservationId = cart?.reservations!![0]?.id;

            let showpi = false;

            const acceptUrl = `${confirmationUrl}?s=s&r=${reservationId}&wu=${getWebUser()}`;
            const declineUrl = `${reservationUrl}&s=d&session=${sessionId}&r=${reservationId}&pi=${showpi.toString()}&wu=${getWebUser()}`;
            const cancelUrl = `${reservationUrl}&s=c&session=${sessionId}&r=${reservationId}&pi=${showpi.toString()}&wu=${getWebUser()}`;
            const exceptionUrl = `${reservationUrl}&s=e&session=${sessionId}&r=${reservationId}&pi=${showpi.toString()}&wu=${getWebUser()}`;

            const isAutomaticPayment = true;
            const input: CartPaymentMethodInput = {
              bookingStatus: BookingStatus.Guaranteed,
              ccardType: parseInt(selectedPaymentOption ?? "1"),
              paymentMode: "CB",
              paymentOptions: "CB",
              automaticPayment: true, //cart.payment.isAuto
              //manualPaymentOptions: null,
              //paymentModeSetOnlyFirst: null,
              //paymentAmount: null,
              //chequeNumber: null,
              url: {
                acceptUrl,
                declineUrl,
                cancelUrl,
                exceptionUrl,
              },
            };

            if (isAutomaticPayment) {
              setLocalStorageItem("rsl_step", StepsEnum.PAYMENT.toString());
              client
                .mutate<Mutation, MutationRedirectPaymentGatewayForCartArgs>({
                  mutation: redirectPaymentGatewayForCartMutation(),
                  variables: {
                    session: { name: sessionId!! },
                    input: { input: input },
                  },
                  fetchPolicy: "no-cache",
                })
                .then((response) => {
                  if (response.data?.redirectPaymentGatewayForCart?.gateway) {
                    setIsSaving(false);
                    ReactPixel.track("InitiateCheckout");
                    trackClick(
                      "Tunnel - Paiement",
                      "click bouton",
                      "Bouton - Réserver",
                      ""
                    );
                    window.location.href =
                      response.data.redirectPaymentGatewayForCart?.gateway;
                  }
                })
                .catch((err) => {
                  toast.error(err.message);
                  _errorsSteps.push({
                    stepIndex: activeStep,
                    variant: "warning",
                    children: <>{err.message}</>,
                  });
                  setErrorsSteps(_errorsSteps);
                  setIsSaving(false);
                });
            } else {
              save(false, cart?.fromFastCheckinCheckout ?? false, (err) => {
                if (err) {
                  toast.error(err.message);
                  _errorsSteps.push({
                    stepIndex: activeStep,
                    variant: "warning",
                    children: <>{err.message}</>,
                  });
                  setErrorsSteps(_errorsSteps);
                  setIsSaving(false);
                } else {
                  trackClick(
                    "Tunnel - Paiement",
                    "click bouton",
                    "Bouton - Réserver",
                    ""
                  );
                  ReactPixel.track("InitiateCheckout");
                  window.location.href = confirmationUrl;
                }
              });
            }
          }
          break;
      }
    }
  };
  //Vérifie que les infos des différents occupants sont ok
  const checkOccupant = (): {
    valid: boolean;
    controlToFocus: string | null;
    hasPrestaOccupant: boolean;
  } => {
    let valid = false;
    let controlToFocus: string | null = null;
    let hasPrestaOccupant = false;
    cart?.reservations?.forEach((reservation) => {
      reservation?.stays?.forEach((stay) => {
        stay?.occupants?.forEach((occupant) => {
          hasPrestaOccupant =
            (occupant?.lineItems?.filter(
              (x) =>
                x?.product?.distributionType ===
                ProductDistributionType.Occupant
            ).length ?? -1) > 0;
          valid = isValidOccupant(
            occupant?.firstName!!,
            occupant?.lastName!!,
            occupant?.birthDate!!
          );
          if (!valid) {
            if (!controlToFocus) {
              if (isEmpty(occupant?.birthDate))
                controlToFocus = `sdr_SingleDatePicker_${occupant?.guiid}`;
              if (isEmpty(occupant?.lastName))
                controlToFocus = `txt_lastname_${occupant?.guiid}`;
              if (isEmpty(occupant?.firstName))
                controlToFocus = `txt_firstname_${occupant?.guiid}`;
            }
          }
        });
      });
    });
    return { valid, controlToFocus, hasPrestaOccupant };
  };
  //Vérifie que les infos d'un occupant sont ok
  const isValidOccupant = (
    firstName: string,
    lastName: string,
    birthDate: Date
  ): boolean => {
    return !isEmpty(firstName) && !isEmpty(lastName) && !isEmpty(birthDate);
  };
  useLoadingScreen(
    isSaving,
    getLanguageVarValue("LANG_neobooking_saving_reservation_in_progress"),
    getLanguageVarValue("LANG_neobooking_please_wait")
  );
  const onNextStep = () => {
    let next = StepsEnum.OCCUPANT;
    if (!hasPresta && !hasPrestaStay) {
      next = StepsEnum.PAYMENT;
    } else if (hasPresta && !hasPrestaStay) {
      next = StepsEnum.OCCUPANT;
    } else if (!hasPresta && hasPrestaStay) {
      next = StepsEnum.ACTIVITIES;
    } else if (hasPresta && hasPrestaStay) {
      next = StepsEnum.ACTIVITIES;
    }
    setActiveStep(next);
  };

  const handleSignout = () => {
    const toastLogout = toast.loading(
      getLanguageVarValue("LANG_neobooking_logout_in_progress")
    );
    signout((err) => {
      if (!err) {
        toast.success(
          getLanguageVarValue("LANG_neobooking_logout_successfully"),
          {
            id: toastLogout,
          }
        );
        redirectTo(window.location.origin);
      } else
        toast.error(err.message, {
          id: toastLogout,
        });
    });
  };

  if (userLoading || loadingCart) return <LoadingScreen />;

  return (
    <>
      {showPopupNoInsurance && (
        <Popup
          showBtnClose={false}
          enableClose={false}
          open={showPopupNoInsurance}
          onClose={() => setShowPopupNoInsurance(false)}
          btnElement={(onClick) => <></>}
        >
          <NoInsuranceContent
            setOpen={setShowPopupNoInsurance}
            setSelectedInsurance={setSelectedInsurance}
            onNextStep={handleNextStep}
          />
        </Popup>
      )}
      <div className="tw-container tw-mx-auto">
        <div
          className={`${
            mode === "vacanceole"
              ? "tw-bg-v_turquoise tw-text-v_blue-dark"
              : "tw-bg_corseole-secondary tw-text-white"
          } tw-flex tw-justify-between tw-py-2 tw-px-2 tw-mb-1`}
        >
          <a href={getCustomerUrlByWebUser(getWebUser(), mode)}>
            <img
              className="tw-w-20 md:tw-w-36"
              src={getAbsoluteImagePath(
                `/assets/img/customer/${
                  mode === "vacanceole" ? "Vacanceole-B-RVB.png" : logo
                }`
              )}
            ></img>
          </a>
          {user && (
            <button
              className="tw-flex tw-flex-col tw-justify-center tw-items-center hover:tw-underline"
              onClick={handleSignout}
              type="button"
            >
              <img
                className="tw-h-5 tw-w-5"
                src={getAbsoluteImagePath("/assets/img/icons/deconnexion.svg")}
              ></img>
              {getLanguageVarValue("LANG_neobooking_me_signout")}
            </button>
          )}
        </div>
        <div className="tw-flex tw-relative tw-left-1">
          <ul id="progress" className="tw-w-full">
            {steps.map((step, index) => {
              return (
                <li
                  className={classNames(
                    index === activeStep && "active",
                    "tw-uppercase"
                  )}
                >
                  {step.label}
                </li>
              );
            })}
          </ul>
        </div>
        {/*<Stepper
          className="tw-flex tw-flex-wrap tw-gap-y-2 tw-w-full tw-bg-red-200 tw-py-10"
          activeStep={activeStep}
          classes={{ root: "MuiStepperFCI-root" }}
        >
          {steps.map((step, index) => {
            return (
              <Step
                key={`${step.label}_${index}`}
                classes={{ root: !step.show ? "patchMuiStep" : "" }}
                onClick={() => {
                  step.show && activeStep > index && setActiveStep(index);
                }}
                className="tw-cursor-pointer"
              >
                <StepLabel>
                  <span className="tw-text-xs sm:tw-text-sm md:tw-text-base tw-text-black tw-border">
                    {step.show ? step.label : ""}
                  </span>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>*/}
        {cart && !isEmpty(cart.reservations) ? (
          <>
            <div className="tw-my-8">
              {activeErrorsSteps && (
                <div className="tw-mb-8">
                  <Alert
                    variant={activeErrorsSteps.variant}
                    dismissable={false}
                  >
                    {activeErrorsSteps.children}
                  </Alert>
                </div>
              )}
              <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-items-start">
                <div className="tw-block md:tw-hidden tw-col-span-12">
                  <Popup
                    btnElement={(onClick) => (
                      <div
                        className={`tw-border ${
                          mode === "vacanceole"
                            ? "tw-border-primary-500"
                            : "tw-border-primary_corseole-500"
                        } tw-p-2`}
                      >
                        <div className="tw-flex tw-items-center">
                          <div className="tw-flex-auto tw-flex tw-flex-col tw-gap-3">
                            <div>
                              <div className="tw-flex tw-items-start">
                                <div
                                  className="tw-cursor-pointer tw-flex-auto"
                                  onClick={onClick}
                                >
                                  {cart?.reservations![0]?.property?.label}
                                </div>
                              </div>
                              <div
                                onClick={onClick}
                                className="tw-cursor-pointer tw-font-bold"
                              >
                                {
                                  cart?.reservations![0]?.property?.address
                                    ?.city
                                }
                              </div>
                            </div>
                            <div
                              className="tw-cursor-pointer"
                              onClick={onClick}
                            >
                              <div className="tw-text-sm tw-font-semibold">
                                {getReservationRoomsInfosName(
                                  cart?.reservations![0]!!
                                )}
                              </div>
                              <div className="tw-text-sm">
                                {dateFormat(
                                  new Date(cart?.reservations![0]!!.startDate),
                                  "dd/mm/yy"
                                )}{" "}
                                <span className="tw-lowercase">
                                  {getLanguageVarValue(
                                    "LANG_neobooking_to_date"
                                  )}
                                </span>{" "}
                                {dateFormat(
                                  new Date(cart?.reservations![0]!!.endDate),
                                  "dd/mm/yy"
                                )}{" "}
                                - {cart?.reservations![0]!!.nbDays}{" "}
                                <span className="tw-lowercase">
                                  {getLanguageVarValue(
                                    "LANG_neobooking_nights"
                                  )}
                                </span>
                              </div>
                              <div className="tw-inline">
                                <button
                                  onClick={onClick}
                                  className={`tw-italic tw-font-bold tw-font-dosis focus:tw-outline-none tw-text-sm ${
                                    mode === "vacanceole"
                                      ? "tw-text-red-500 hover:tw-text-red-400"
                                      : "tw-text_corseole-secondary"
                                  }`}
                                >
                                  {getLanguageVarValue(
                                    "LANG_neobooking_see_more"
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="tw-cursor-pointer tw-flex-none tw-font-bold tw-flex tw-flex-col">
                            <PopupEdit
                              btnElement={(onClick) => (
                                <button
                                  onClick={onClick}
                                  className={`tw-flex-none ${
                                    mode === "vacanceole"
                                      ? "tw-text-red-500 hover:tw-text-red-400"
                                      : "tw-text_corseole-secondary"
                                  } tw-italic tw-text-sm tw-pl-2 focus:tw-outline-none`}
                                >
                                  +{" "}
                                  {getLanguageVarValue("LANG_neobooking_edit")}
                                </button>
                              )}
                            />
                            <div onClick={onClick}>
                              {cart?.reservations![0]?.totalAmount?.amount?.toFixed(
                                2
                              )}
                              {getCurrencySymbol(
                                cart?.reservations![0]?.totalAmount
                                  ?.currencyCode!!
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  >
                    <BasketSummary
                      reservations={cart?.reservations!!}
                      enableEdit={false}
                      selectedInsurance={selectedInsurance}
                      setSelectedInsurance={setSelectedInsurance}
                    />
                  </Popup>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-8">
                  <>
                    {!isFromFastCheckinOrCheckout ? (
                      <>
                        {activeStep === StepsEnum.AUTH ? (
                          <>
                            <GTMReservationPageStep1 cart={cart} />
                            <StepAuth
                              showContinueWithoutLogin={
                                hasPresta || hasPrestaStay
                              }
                              onNextStep={onNextStep}
                            />
                          </>
                        ) : activeStep === StepsEnum.ACTIVITIES ? (
                          <>
                            <GTMReservationPageStep2 cart={cart} />
                            <StepPrestation />
                          </>
                        ) : activeStep === StepsEnum.OCCUPANT ? (
                          <>
                            <GTMReservationPageStep3 cart={cart} />
                            <StepReservationContent />
                          </>
                        ) : (
                          activeStep === StepsEnum.PAYMENT && (
                            <>
                              {!user ? (
                                <div className="tw-mb-4">
                                  <StepAuth
                                    showContinueWithoutLogin={false}
                                    onNextStep={() => undefined}
                                  />
                                </div>
                              ) : (
                                <>
                                  <GTMReservationPageStep4 cart={cart} />
                                  <StepPayment
                                    termsChecked={termsChecked}
                                    setTermsChecked={setTermsChecked}
                                    termsError={termsError}
                                    setTermsError={setTermsError}
                                    paymentDueTypeSelected={
                                      /*selectedPaymentDueType*/ undefined
                                    }
                                    setPaymentDueTypeSelected={
                                      /*setSelectedPaymentDueType*/ undefined
                                    }
                                    selectedPaymentMethod={
                                      selectedPaymentMethod
                                    }
                                    setSelectedPaymentMethod={
                                      setSelectedPaymentMethod
                                    }
                                    selectedPaymentOption={
                                      selectedPaymentOption
                                    }
                                    setSelectedPaymentOption={
                                      setSelectedPaymentOption
                                    }
                                    displayInsurance={true}
                                    selectedInsurance={selectedInsurance}
                                    setSelectedInsurance={setSelectedInsurance}
                                  />
                                </>
                              )}
                            </>
                          )
                        )}{" "}
                        {/* fast checking steps */}
                      </>
                    ) : (
                      <>
                        {activeStep === 0 ? (
                          <>
                            <StepReservationContent
                              showProductOccupant={false}
                            />
                          </>
                        ) : activeStep === 1 ? (
                          <>
                            <StepPrestation />
                          </>
                        ) : (
                          activeStep === 2 && (
                            <StepPayment
                              showDiscounts={false}
                              termsChecked={termsChecked}
                              setTermsChecked={setTermsChecked}
                              termsError={termsError}
                              displayInsurance={false}
                              setTermsError={setTermsError}
                              paymentDueTypeSelected={
                                /*selectedPaymentDueType*/ undefined
                              }
                              setPaymentDueTypeSelected={
                                /*setSelectedPaymentDueType*/ undefined
                              }
                              selectedPaymentMethod={selectedPaymentMethod}
                              setSelectedPaymentMethod={
                                setSelectedPaymentMethod
                              }
                              selectedPaymentOption={selectedPaymentOption}
                              setSelectedPaymentOption={
                                setSelectedPaymentOption
                              }
                              selectedInsurance={selectedInsurance}
                              setSelectedInsurance={setSelectedInsurance}
                              showPaymentDue={false}
                            />
                          )
                        )}
                      </>
                    )}
                  </>

                  <div className="tw-fixed tw-bottom-0 tw-flex tw-w-full tw-left-0 tw-gap-6 tw-justify-center tw-py-6 tw-bg-white tw-shadow-[0px_-1px_10px_0px] tw-shadow-gray-500  tw-z-10">
                    {/* STEPPER FOOTER ACTION BACK/NEXT */}
                    <Button
                      type="button"
                      variant="gradient"
                      className={
                        "tw-bg-yellow-100 tw-text-yellow-500 disabled:tw-opacity-50 tw-text-sm md:tw-text-base tw-font-medium tw-py-2 tw-px-8 tw-uppercase tw-rounded"
                      }
                      disabled={activeStep === 0}
                      isLoading={savingStep}
                      additionalClassname="tw-my-2 tw-ml-auto tw-block"
                      onClick={handlePreviousStep}
                    >
                      {getLanguageVarValue("LANG_neobooking_back_step")}
                    </Button>
                    <Button
                      type="button"
                      className={
                        "tw-bg-yellow-100 tw-text-yellow-500 tw-text-sm md:tw-text-base tw-font-medium tw-py-2 tw-px-8 tw-uppercase tw-rounded"
                      }
                      onClick={() => handleNextStep(true)}
                    >
                      {getLanguageVarValue(
                        activeStep === stepFast.length - 1
                          ? "VALIDER PRECHECKIN"
                          : "LANG_neobooking_next_step"
                      )}
                    </Button>
                  </div>
                </div>
                <div
                  className={`tw-hidden md:tw-block md:tw-col-span-4 tw-sticky tw-top-20 tw-bg-white tw-border ${
                    mode === "vacanceole"
                      ? "tw-border-primary-600"
                      : "tw-border-primary_corseole-600"
                  }`}
                >
                  <BasketSummary
                    border={false}
                    enableEdit={true}
                    reservations={cart?.reservations!!}
                    title={getLanguageVarValue("LANG_neobooking_my_cart")}
                    selectedInsurance={selectedInsurance}
                    setSelectedInsurance={setSelectedInsurance}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="tw-flex tw-justify-center tw-items-center tw-flex-col tw-gap-4 tw-w-full">
            <div className="tw-border tw-p-12 tw-text-center tw-w-full">
              <p className="tw-mb-6">
                {getLanguageVarValue("LANG_neobooking_no_dispo_message")}
              </p>
              <LinkButton
                href={window.location.origin}
                additionalClassname="tw-max-w-md tw-mx-auto tw-uppercase"
              >
                {getLanguageVarValue("LANG_neobooking_new_search")}
              </LinkButton>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
const languagesVarsCodesPrestation = [
  "LANG_neobooking_no_service_available", //Aucun service disponible
  "LANG_neobooking_custom_stay", //Personnaliser votre séjour
  "LANG_neobooking_custom_dossier", //Personnaliser votre dossier
];
const StepPrestation: React.FC = () => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();
  const { cart } = useCart();
  useScrollTop();
  return (
    <div className="">
      {cart?.reservations?.map((x, i) => {
        return (
          <React.Fragment key={`cart_${x?.cartItemID}_${i}`}>
            <ProductList
              excludeCategories={["ASSUR"]}
              mode="global"
              showCriteria="PRESTATIONS"
              cartItemID={x?.cartItemID!!}
              emptyMessage={getLanguageVarValue(
                "LANG_neobooking_no_service_available"
              )}
              title={
                <div className="tw-titre-principal-dosis">
                  {getLanguageVarValue("LANG_neobooking_custom_dossier")}
                </div>
              }
            />
            {x?.stays?.map((stay, stayIndex) => {
              return (
                <React.Fragment key={`cart_${x?.cartItemID}_${i}`}>
                  <ProductList
                    loadingChildren={<LoadingProduct />}
                    excludeCategories={["ASSUR"]}
                    mode="stay"
                    title={
                      !isEmpty(x.stays) && x.stays?.length!! > 1 ? (
                        <div className="tw-titre-principal-dosis">
                          {!isEmpty(stay?.distribution?.roomTypes) &&
                            stay?.distribution?.roomTypes?.[0]?.label}
                        </div>
                      ) : (
                        <div className="tw-titre-principal-dosis">
                          {getLanguageVarValue("LANG_neobooking_custom_stay")}
                        </div>
                      )
                    }
                    key={`cart_${x?.cartItemID}_${i}_stay_${stayIndex}`}
                    cartItemID={x?.cartItemID!!}
                    stayGuiid={stay?.guiid!!}
                    emptyMessage={getLanguageVarValue(
                      "LANG_neobooking_no_service_available"
                    )}
                    showCriteria="PRESTATIONS"
                  />
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
};
const LoadingProduct = () => {
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";

  return (
    <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-gap-4">
      <div role="status">
        <svg
          className={`tw-inline tw-mr-2 tw-w-10 tw-h-10 tw-text-gray-200 tw-animate-spin ${
            mode === "vacanceole"
              ? "tw-fill-primary-500"
              : "tw-fill-primary_corseole-500"
          }`}
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="tw-sr-only">Loading...</span>
      </div>
    </div>
  );
};
type NoInsuranceContentProps = {
  setSelectedInsurance: (insurance: string | undefined) => void;
  setOpen: (open: boolean) => void;
  onNextStep: () => void;
};
const NoInsuranceContent: React.FC<NoInsuranceContentProps> = ({
  setSelectedInsurance,
  setOpen,
  onNextStep,
}) => {
  /* Hooks */
  const client = useApolloClient();
  const { getLanguageVarValue, sessionId } = useSession();

  /* States */
  const [insuranceProducts, setInsuranceProducts] = useState<
    ProductByCategory[]
  >([]);

  /* Functions */
  const handleSetNoInsurance = () => {
    onNextStep();
    setOpen(false);
  };

  const handleDoChoice = () => {
    setOpen(false);
    scrollTop();
  };

  useEffect(() => {
    client
      .query<Query, QueryGetBookingProductsArgs>({
        query: getBookingProductsQuery(),
        variables: {
          session: { name: sessionId },
          input: {
            categoryCodes: ["ASSUR"],
          },
        },
        fetchPolicy: "no-cache",
      })
      .then((res) => {
        if (res.data.getBookingProducts?.bookingProducts) {
          setInsuranceProducts(
            res.data.getBookingProducts?.bookingProducts as ProductByCategory[]
          );
        }
      });
  }, []);

  return (
    <div className="tw-px-8">
      <h3 className="tw-titre-principal-dosis tw-text-center tw-font-bold">
        {getLanguageVarValue("LANG_neobooking_insurance_holiday")}
      </h3>
      <p className="tw-text-orange-500 tw-text-center tw-my-4">
        {getLanguageVarValue("LANG_neobooking_select_insurance")}
      </p>
      <div className="shadow-custom tw-p-4">
        <p className="tw-mb-4">
          {getLanguageVarValue("LANG_neobooking_info_insurance")}
        </p>
        <ul>
          <li className="tw-flex tw-mb-2">
            <img
              className="tw-w-6 tw-inline-block tw-mr-2"
              src={getAbsoluteImagePath("/assets/img/icons/validation.svg")}
            ></img>
            <span>
              {getLanguageVarValue(
                "LANG_neobooking_insurance_cancellation_stay"
              )}
            </span>
          </li>
          <li className="tw-flex tw-mb-2">
            <img
              className="tw-w-6 tw-inline-block tw-mr-2"
              src={getAbsoluteImagePath("/assets/img/icons/validation.svg")}
            ></img>
            <span>
              {getLanguageVarValue(
                "LANG_neobooking_insurance_changes_vacation_dates"
              )}
            </span>
          </li>
          <li className="tw-flex tw-mb-2">
            <img
              className="tw-w-6 tw-inline-block tw-mr-2"
              src={getAbsoluteImagePath("/assets/img/icons/validation.svg")}
            ></img>
            <span>
              {getLanguageVarValue("LANG_neobooking_insurance_job_transfer")}
            </span>
          </li>
          <li className="tw-flex tw-mb-2">
            <img
              className="tw-w-6 tw-inline-block tw-mr-2"
              src={getAbsoluteImagePath("/assets/img/icons/validation.svg")}
            ></img>
            <span>
              {getLanguageVarValue("LANG_neobooking_insurance_covid")}
            </span>
          </li>
        </ul>
      </div>
      <Button
        type="button"
        variant="gradient"
        onClick={handleDoChoice}
        additionalClassname="tw-my-4 tw-ml-auto tw-block !tw-w-full"
      >
        {getLanguageVarValue("LANG_neobooking_make_my_choice")}
      </Button>
      <div className="tw-text-center">
        <button
          onClick={handleSetNoInsurance}
          className="tw-underline tw-font-dosis tw-font-semibold tw-text-xl tw-underline-offset-[6px]"
        >
          {getLanguageVarValue("LANG_neobooking_continue_without_insurance")}
        </button>
      </div>
    </div>
  );
};
const ButtonOriginUrl = () => {
  /* Hooks */
  const { getLanguageVarValue, setSessionId } = useSession();
  const { clear } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const origin_url = searchParams.get("originUrl");
  const handleRedirect = () => {
    trackClick(
      "Tunnel - Panier",
      "click bouton",
      "Bouton - Réserver un autre séjour",
      ""
    );
    //on remove la proposal key et on clear le cart au retour sur choix hébergement
    clear(() => {});
    removeLocalStorageItem("rsl_proposal_key");
    window.location.href = origin_url!!;
  };
  return origin_url ? (
    <Button
      type="button"
      className="tw-font-dosis tw-underline tw-underline-offset-[6px] tw-font-semibold tw-mt-2"
      onClick={handleRedirect}
    >
      {getLanguageVarValue("LANG_neobooking_back")}
    </Button>
  ) : (
    <></>
  );
};
export default FastCheckinPage;
